<template>
  <div class="chart-wrapper print-chart-wrapper mt-15">
    <LoadingAnimation v-if="loading" />
    <div class="chart-content" v-else>
      <div class="hour-title">
        <span class="hour-item pt-2 pb-2 active">
          <img class="inline-block" src="@/assets/image/time-icon.png" />
        </span>
        <span
          v-for="hour in hours"
          :key="hour.value"
          class="hour-item pt-2 pb-2"
          :class="hour.active ? 'active' : ''"
          >{{ hour.show ? hour.label : '' }}
        </span>
      </div>
      <div v-for="(day, index) in chartData" :key="day.Date" class="day-item">
        <div class="day-title">
          <div>Day {{ index + 1 }}</div>
          <div>
            {{
              userLang.indexOf('en') > -1
                ? moment(day.Date).format('DD MMM YY')
                : moment(day.Date).format('DD/MM/YY')
            }}
          </div>
        </div>
        <div class="day-icon-content" v-if="day.Data.length">
          <!-- pad cycle -->
          <div
            v-for="dayItem in day.Data"
            :key="dayItem.PadCycleId + dayItem.StartToToday"
            class="day-icon-item"
            :style="{
              left: (dayItem.StartToToday / dayDuration) * 100 + '%',
              width: (dayItem.BlockWidth / dayDuration) * 100 + '%'
            }"
          >
            <div
              class="pad-cycle-item"
              :class="{
                'pad-start': dayItem.IsStarted === padCycleStartType.isStart,
                'pad-end':
                  dayItem.IsCompleted ===
                    padCycleEndType.isCompletedImmediate ||
                  dayItem.IsCompleted === padCycleEndType.isCompleted
              }"
            ></div>
          </div>
          <!-- void event in pad -->
          <div
            v-for="dayItem in day.Data"
            :key="dayItem.PadCycleId + dayItem.StartToToday + 'event'"
          >
            <div
              v-for="(item, index) in dayItem.Voids"
              :key="item.TimeStamp + item.Status"
            >
              <!-- duration -->
              <el-tooltip
                v-if="item.Duration"
                placement="top"
                effect="light"
                popper-class="event-detail-popup"
              >
                <!-- poper content -->
                <div slot="content">
                  <div class="event-item">
                    <div
                      class="smart-event-icon left-side"
                      :class="getEventInfo(item.Status).class"
                    >
                      <img
                        v-if="getEventInfo(item.Status).imgSrc"
                        :src="getEventInfo(item.Status).imgSrc"
                      />
                    </div>
                    <div class="inline-block">
                      <div class="status-text secondary-font">
                        {{
                          getEventInfo(item.Status).text | firstLetterCapitalize
                        }}
                      </div>
                      <div>
                        {{ moment(item.TimeStamp).format('h:mm A') }}
                      </div>
                      <div>
                        <div class="status-text mt-5 secondary-font">
                          {{ $t('text.duration') }}
                        </div>
                        <div>{{ item.Duration | secondFormat }}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  :style="{
                    left: (item.StartToToday / dayDuration) * 100 + '%',
                    width: (item.Duration / dayDuration) * 100 + '%'
                  }"
                  class="void-event-with-duration"
                >
                  <div
                    v-if="item.Status === voidConst.outOfRange"
                    class="lost-pod-connection-item"
                  ></div>
                  <div v-else class="external-humidity">
                    <img
                      class="start-external-icon"
                      src="@/assets/image/VoidingPatternIcon/external-humidity.png"
                    />
                    <img
                      class="end-external-icon"
                      src="@/assets/image/VoidingPatternIcon/external-humidity.png"
                    />
                  </div>
                </div>
              </el-tooltip>
              <!-- icon -->
              <el-tooltip
                v-else
                placement="top"
                effect="light"
                popper-class="event-detail-popup"
              >
                <!-- poper content -->
                <div slot="content">
                  <div class="event-item">
                    <div
                      class="event-icon left-side"
                      :class="getEventInfo(item.Status).class"
                    >
                      <img
                        v-if="getEventInfo(item.Status).imgSrc"
                        :src="getEventInfo(item.Status).imgSrc"
                      />
                    </div>
                    <div class="inline-block">
                      <div class="status-text secondary-font">
                        {{
                          getEventInfo(item.Status).text | firstLetterCapitalize
                        }}
                      </div>
                      <div>{{ moment(item.TimeStamp).format('h:mm A') }}</div>
                      <div v-if="item.Duration">
                        <div class="status-text mt-5 secondary-font">
                          {{ $t('text.duration') }}
                        </div>
                        <div>{{ item.Duration | secondFormat }}</div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="event-item"
                    v-for="subEvent in item.SubEvents"
                    :key="subEvent.TimeStamp + subEvent.Status"
                  >
                    <div
                      class="event-icon left-side"
                      :class="getEventInfo(subEvent.Status).class"
                    >
                      <img
                        v-if="getEventInfo(subEvent.Status).imgSrc"
                        :src="getEventInfo(subEvent.Status).imgSrc"
                      />
                    </div>
                    <div class="inline-block">
                      <div class="status-text secondary-font">
                        {{
                          getEventInfo(subEvent.Status).text
                            | firstLetterCapitalize
                        }}
                      </div>
                      <div>
                        {{ moment(subEvent.TimeStamp).format('h:mm A') }}
                      </div>
                    </div>
                  </div>
                </div>
                <!-- There are multiple events near this icon, which are displayed in combination -->
                <!-- only show the top icon and the bottom icon's edge in the chart -->
                <div
                  v-if="item.SubEvents.length"
                  class="stack-icon-wrapper-in-tooltip"
                  :style="{
                    left: getEventPosition(dayItem, index)
                  }"
                >
                  <!-- bottom icon's edge -->
                  <div
                    class="event-icon"
                    :class="getEventInfo(item.Status).class"
                  ></div>
                  <!-- top icon -->
                  <div
                    class="event-icon top-icon"
                    :class="
                      getEventInfo(
                        item.SubEvents[item.SubEvents.length - 1].Status
                      ).class
                    "
                  >
                    <img
                      v-if="
                        getEventInfo(
                          item.SubEvents[item.SubEvents.length - 1].Status
                        ).imgSrc
                      "
                      :src="
                        getEventInfo(
                          item.SubEvents[item.SubEvents.length - 1].Status
                        ).imgSrc
                      "
                    />
                  </div>
                </div>
                <!-- Single event -->
                <div
                  v-else
                  class="event-icon"
                  :style="{
                    left: getEventPosition(dayItem, index)
                  }"
                  :class="getEventInfo(item.Status).class"
                >
                  <img
                    v-if="getEventInfo(item.Status).imgSrc"
                    :src="getEventInfo(item.Status).imgSrc"
                  />
                </div>
              </el-tooltip>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { images, voidState, hours } from '@/const.js'
import LoadingAnimation from '@/components/patients/charts/LoadingAnimation'
import {
  padCycleEndType,
  padCycleStartType,
  voidConst
} from '@/components/patients/charts/smartzEventsChart/SmartzEventConst.js'
export default {
  data() {
    return {
      images,
      voidState,
      moment,
      userLang: navigator.language || navigator.userLanguage,
      hours,
      dayDuration: 86400,
      padCycleEndType,
      padCycleStartType,
      voidConst
    }
  },
  props: {
    chartData: {
      type: Array,
      require: true
    },
    loading: {
      type: Boolean,
      require: true
    }
  },
  components: {
    LoadingAnimation
  },
  computed: {
    getEventPosition() {
      return (dayItem, index) => {
        let currentEvent = dayItem.Voids[index]
        let left = (currentEvent.StartToToday / this.dayDuration) * 100 + '%'
        // If it is an icon at the end of the pad cycle, move it to the right and do not exceed the pad cycle
        if (
          moment(dayItem.EndDate).diff(
            moment(currentEvent.TimeStamp),
            'minutes'
          ) < 20
        ) {
          let endCyclePosition =
            (moment(dayItem.EndDate).diff(
              moment(dayItem.EndDate).format('YYYY-MM-DD 00:00:00'),
              'seconds'
            ) /
              this.dayDuration) *
              100 +
            '%'
          left = `calc(${endCyclePosition} - 24px)`
        }
        return left
      }
    },
    getEventInfo() {
      return type => {
        let info
        switch (type) {
          case this.voidConst.small:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/small-void.png'),
              class: 'void-event',
              text: this.$t('text.small_void')
            }
            break
          case this.voidConst.medium:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/medium-void.png'),
              class: 'void-event',
              text: this.$t('text.medium_void')
            }
            break
          case this.voidConst.large:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/large-void.png'),
              class: 'void-event',
              text: this.$t('text.large_void')
            }
            break
          case this.voidConst.diaperNotSelected:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/diaper-not-selected.png'),
              class: 'diaper-not-selected',
              text: this.$t('text.diaper_not_selected')
            }
            break
          case this.voidConst.external:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/external-humidity.png'),
              class: 'external-humidity',
              text: this.$t('text.external_humidity')
            }
            break
          case this.voidConst.outOfRange:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/out-of-range.png'),
              class: 'out-of-range',
              text: this.$t('text.out_of_range')
            }
            break
          case this.voidConst.padStart:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/pad-fullness1.png'),
              class: 'pad-fullness',
              text: this.$t('text.pad_start')
            }
            break
          case this.voidConst.padEndLevel1:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/pad-fullness1.png'),
              class: 'pad-fullness',
              text: this.$t('text.pad_end')
            }
            break
          case this.voidConst.padEndLevel2:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/pad-fullness2.png'),
              class: 'pad-fullness',
              text: this.$t('text.pad_end')
            }
            break
          case this.voidConst.padEndLevel3:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/pad-fullness3.png'),
              class: 'pad-fullness',
              text: this.$t('text.pad_end')
            }
            break
          case this.voidConst.padEndLevel4:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/pad-fullness4.png'),
              class: 'pad-fullness',
              text: this.$t('text.pad_end')
            }
            break
          case this.voidConst.padEndLevel5:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/pad-fullness5.png'),
              class: 'pad-fullness',
              text: this.$t('text.pad_end')
            }
            break
          default:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/small-void.png'),
              class: 'void-event',
              text: this.$t('text.small_void')
            }
        }
        return info
      }
    }
  }
}
</script>
